import { debugChatLinks } from '../chatLinkDebug';
import { MAX_LINKS_PER_MESSAGE } from './constants';
import { phraseMatchPattern, phraseWordCount } from './phraseUtils';
import { disambiguateTargetsAtSpan, extendMatchSpanWithFollowingWords } from './spanMatching';
import type { ChatLinkTarget, ChatTextSegment } from './types';

export function findChatLinksInText(text: string, targets: ChatLinkTarget[]): ChatTextSegment[] {
  if (!text) return [{ type: 'text', value: '' }];
  if (!targets.length) return [{ type: 'text', value: text }];

  const phraseEntries: Array<{ phrase: string; targets: ChatLinkTarget[] }> = [];
  const phraseToTargets = new Map<string, { phrase: string; targets: ChatLinkTarget[] }>();

  for (const target of targets) {
    for (const phrase of target.matchPhrases) {
      const key = phrase.toLowerCase();
      const existing = phraseToTargets.get(key);
      if (!existing) {
        phraseToTargets.set(key, { phrase, targets: [target] });
        continue;
      }
      if (!existing.targets.some((entry) => entry.id === target.id)) {
        existing.targets.push(target);
      }
    }
  }

  phraseEntries.push(...phraseToTargets.values());
  phraseEntries.sort((left, right) => {
    const wordDiff = phraseWordCount(right.phrase) - phraseWordCount(left.phrase);
    if (wordDiff !== 0) return wordDiff;
    return right.phrase.length - left.phrase.length;
  });

  const matches: Array<{
    start: number;
    end: number;
    value: string;
    targets: ChatLinkTarget[];
  }> = [];

  for (const entry of phraseEntries) {
    if (matches.length >= MAX_LINKS_PER_MESSAGE) break;

    const regex = phraseMatchPattern(entry.phrase);
    let match: RegExpExecArray | null = regex.exec(text);
    while (match !== null) {
      const start = match.index;
      const end = start + match[0].length;
      const overlaps = matches.some(
        (existing) => !(end <= existing.start || start >= existing.end),
      );
      if (!overlaps) {
        const disambiguated = disambiguateTargetsAtSpan(text, start, end, targets);
        if (disambiguated.length === 0) {
          match = regex.exec(text);
          continue;
        }
        const extended = extendMatchSpanWithFollowingWords(text, start, end, targets);
        matches.push({
          start,
          end: extended.end,
          value: text.slice(start, extended.end),
          targets: extended.targets,
        });
        if (matches.length >= MAX_LINKS_PER_MESSAGE) break;
      }
      match = regex.exec(text);
    }
  }

  if (matches.length === 0) {
    return [{ type: 'text', value: text }];
  }

  matches.sort((left, right) => left.start - right.start);

  const segments: ChatTextSegment[] = [];
  let cursor = 0;

  for (const match of matches) {
    if (match.start > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, match.start) });
    }
    segments.push({ type: 'link', value: match.value, targets: match.targets });
    cursor = match.end;
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) });
  }

  const links = segments.filter((segment) => segment.type === 'link');
  debugChatLinks('message scanned', {
    textPreview: text.length > 80 ? `${text.slice(0, 80)}…` : text,
    targetCount: targets.length,
    segmentCount: segments.length,
    linkCount: links.length,
    links: links.map((segment) => ({
      value: segment.value,
      targetIds: segment.targets?.map((target) => target.id),
      navigateUrls: segment.targets?.map((target) => target.navigateUrl),
    })),
  });

  return segments;
}
